import type RootState from 'app-components/utils/redux-store/rootState';
import { createSelector } from 'reselect';
import { CUSTOMERS_REDUCER_KEY, type CustomerProps } from './customersTypes';

type State = Pick<RootState, typeof CUSTOMERS_REDUCER_KEY>;

export const getCustomersStore = (state: State) => state[CUSTOMERS_REDUCER_KEY];
export const getResourcesById = (state: State) => getCustomersStore(state).resourcesById;
export const getLoadPending = (state: State) => getCustomersStore(state).loadPending;
export const getIsLoading = (state: State) => getCustomersStore(state).isLoading;
export const getError = (state: State) => getCustomersStore(state).error;
export const getLastSyncDate = (state: State) => getCustomersStore(state).lastSyncDate;

export const getCustomersWithArchived = getResourcesById;

export const getCustomers = createSelector(getResourcesById, (customers) => customers?.filter((c) => !c.isArchived));

export const getCustomersList = createSelector(getCustomers, (customers) => customers?.toList());

export const getCustomerById = createSelector(
  getCustomersWithArchived,
  (_state: unknown, customerId: string) => customerId,
  (customers, id) => customers?.get(id),
);

export const getCustomerNameById = createSelector(getCustomerById, (customer) =>
  (customer?.businessName || `${customer?.firstName ?? ''} ${customer?.lastName ?? ''}`).trim(),
);

export function formatAddress(customerWithAddress?: CustomerProps | null): string | null {
  if (!customerWithAddress) return null;
  const { address } = customerWithAddress;

  const parts: string[] = [];
  if (address?.fullName) {
    parts.push(address.fullName);
  } else if (customerWithAddress.firstName || customerWithAddress.lastName) {
    parts.push([customerWithAddress.firstName, customerWithAddress.lastName].filter(Boolean).join(' '));
  }

  if (address?.fullName) parts.push(address.fullName);
  if (address?.line1) parts.push(address.line1);
  if (address?.line2) parts.push(address.line2);
  if (address?.city || address?.state || address?.zipCode) {
    parts.push([address.city, address.state, address.zipCode].filter(Boolean).join(', '));
  }
  if (address?.country) parts.push(address.country);

  if (customerWithAddress.email) parts.push(customerWithAddress.email);
  if (customerWithAddress.phoneNumber) parts.push(customerWithAddress.phoneNumber);

  return parts.join('\n');
}

export const getFormattedAddressForCustomer = createSelector(getCustomerById, formatAddress);
